/**
 * TanStack Query hooks for Agent form operations.
 *
 * Covers:
 * - Agent CRUD queries/mutations
 * - Skills list query (for picker)
 * - Models list query (for selector)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  agentApi,
  type ListAgentsParams,
  type CreateAgentInput,
  type UpdateAgentInput,
  type SkillType,
  type AgentScope,
} from '@/services/agentApi';

// ---------------------------------------------------------------------------
// Query Keys
// ---------------------------------------------------------------------------

export const agentKeys = {
  all: ['agents'] as const,
  list: (params?: ListAgentsParams) => [...agentKeys.all, 'list', params] as const,
  detail: (id: string) => [...agentKeys.all, 'detail', id] as const,
  skills: (params?: { scope?: AgentScope | 'all'; type?: SkillType }) =>
    ['skills', 'for-agent', params] as const,
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** Fetch agents list */
export function useAgents(params?: ListAgentsParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: agentKeys.list(params),
    queryFn: () => agentApi.list(params),
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  });
}

/** Fetch a single agent by ID (for edit mode) */
export function useAgent(id: string | undefined) {
  return useQuery({
    queryKey: agentKeys.detail(id ?? ''),
    queryFn: () => agentApi.get(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

/** Fetch skills list for the skill picker */
export function useSkillsForPicker(params?: {
  scope?: AgentScope | 'all';
  type?: SkillType;
}) {
  return useQuery({
    queryKey: agentKeys.skills(params),
    queryFn: () =>
      agentApi.listSkills({
        scope: params?.scope ?? 'all',
        type: params?.type,
        per_page: 100,
      }),
    staleTime: 60_000,
    select: (data) => data.data,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/** Create a new agent */
export function useCreateAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateAgentInput) => agentApi.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.all });
    },
  });
}

/** Update an existing agent */
export function useUpdateAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateAgentInput }) =>
      agentApi.update(id, input),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: agentKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: agentKeys.all });
    },
  });
}

/** Delete an agent */
export function useDeleteAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => agentApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.all });
    },
  });
}

/** Activate agent for current user */
export function useActivateAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => agentApi.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.all });
    },
  });
}

/** Deactivate agent for current user */
export function useDeactivateAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => agentApi.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.all });
    },
  });
}

/** Duplicate an agent */
export function useDuplicateAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input?: { new_name?: string; new_slug?: string; scope?: AgentScope } }) =>
      agentApi.duplicate(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.all });
    },
  });
}
